import type { ReactNode } from 'react';
import { getContent as getReactContent } from 'pp-react-l10n';
import {
  hasOwnProp,
  isObject,
  // @ts-expect-error TODO: type-react-components
} from '@checkout/react-components';

import { isFunction } from '@/lib/utils/typeUtils';
import { logger } from '../logger';
import { isTest } from '../utils/env';
import { getGlobals, getLocale, updateLocale } from '../globals';
import { getObjectProperty } from '../utils/miscUtils';
import { redirectToGenericError } from '../utils/redirects';
import type { GenericObjectType } from '@/types';

type ContentCacheEntry = {
  // TODO: Get typings for pp-react-l10n
  content: GenericObjectType;
  hash?: string | null;
};

type ContentCache = {
  [key: string]: ContentCacheEntry;
};

// Initialize the cache with the content and locale from the
// initial data object that was injected by the server.
const CONTENT_CACHE: ContentCache = {
  [getLocale().locale]: {
    content: getGlobals().content,
    hash: getGlobals().contentHash,
  },
};
const MISSING_CONTENT_KEYS = new Set();
const NESTED_CONTENT_KEY_REGEX = /{\w+(?=})/g;
const RTL_LANGUAGES = ['ar', 'fa', 'he'];

/** Gets the current content object */
const getContentFromCache = (locale: string): GenericObjectType => {
  if (!hasCachedContentForLocale(locale)) {
    logger.error('no_content_found_for_locale', { locale });
    return {};
  }

  return CONTENT_CACHE[locale].content;
};

const hasCachedContentForLocale = (locale: string): boolean =>
  hasOwnProp(CONTENT_CACHE, locale);

/**
 * Conditionally updates the content cache. If the cache doesn't
 * have content for the particular locale or if the env is the test env then
 * the cache is updated with the passed argument.
 */
const updateContentCache = ({
  content,
  hash,
  locale,
}: ContentCacheEntry & { locale?: string }): void => {
  if (!isObject(content)) {
    logger.error('content_cache_update_with_no_content');
    // Instead of returning a void function we should just exit or use else
    // not altering in TS convert PR to avoid functionality changes
    return redirectToGenericError();
  }

  const key = locale || getLocale().locale;
  if (isTest || !hasCachedContentForLocale(key)) {
    logger.info('updating_content_cache', { locale: key });
    CONTENT_CACHE[key] = {
      content,
      hash,
    };
  }
};

/**
 * Function that is fired when `getReactContent()` is called
 * with a key name that is not found in the content object. We log the content
 * path and locale for each path/locale pair once.
 * @NOTE
 * This should never be called, since getContentFromCache checks for content
 */
const onContentMissing = ({ path }: GenericObjectType) => {
  const { locale } = getLocale();

  // Prevent missing content from being logged more than once per locale
  if (MISSING_CONTENT_KEYS.has(`${locale}~${path}`)) {
    return;
  }

  MISSING_CONTENT_KEYS.add(`${locale}~${path}`);
  logger.warn(`content_missing_${locale}_${path}`);
};

let __internalGetContent = getReactContent({
  content: getContentFromCache(getLocale().locale),
  onContentMissing,
});

/**
 * Used to reset the content used by the `getContent()` function.
 * This is needed for when a user changes their language.
 */
const resetContent = (): void => {
  const { language, locale } = getLocale();
  const content = getContentFromCache(locale);

  __internalGetContent = getReactContent({
    content,
    onContentMissing,
  });

  /**
   * @TECH_DEBT
   * When using content to populate some dropdown options, namely
   * the occupation list, we check the globals.content property so
   * we need to reset field on lang/locale change. We should probably
   * rethink how we're doing this.
   */
  getGlobals().content = content;

  // Update the `lang` and `dir` attributes on the <html> tag
  const directionality = RTL_LANGUAGES.includes(language) ? 'rtl' : 'ltr';
  const currentDirectionality = document.documentElement.getAttribute('dir');
  document.documentElement.setAttribute('lang', language);
  if (currentDirectionality !== directionality) {
    updateLocale({ directionality });
    document.documentElement.setAttribute('dir', directionality);
  }
};

/**
 * Wrapper around the pp-react-l10n getReactContent
 * function that will ignore return values when they are functions.
 * pp-react-l10n allows for currying content, which we are not using.
 */

type JSXObject = {
  [key: string]:
    | JSX.Element
    | ReactNode[]
    | string
    | number
    | null
    | undefined
    | unknown;
};

const getContent = <T extends (string | JSXObject | null)[]>(
  ...args: T
): T extends string[] ? string : ReactNode[] => {
  const result = __internalGetContent(...args);
  return isFunction(result) ? null : result;
};

/**
 * Used to get a content "group" -- a set of content
 * that is grouped by an array or object. E.G. Some countries have
 * one header for the optional account section while other countries
 * have multiple. They are all grouped under the
 * `signup.optionalAccountDetailsHeading` namespace.
 */
const getContentGroup = (
  path: string,
  defaultValue: GenericObjectType = {},
): GenericObjectType | string => {
  const { locale } = getLocale();
  const content = getContentFromCache(locale);
  const result = getObjectProperty(content, path, { defaultValue });
  // @ts-expect-error not touching functionality during TS conversion
  return result;
};

const getContentHash = (locale: string): string | null => {
  const { hash } = CONTENT_CACHE[locale] || {};
  /**
   * @TECH_DEBIT
   * We weirdly turn null since null is our falsy default used in globals
   */
  return hash || null;
};

/**
 * Takes a content key, e.g. `'compliance.signupTerms'` and returns
 * an array of nested keys the content string accepts, e.g.
 * `['userAgreementLink', 'privacyPolicyLink']`
 */
const getNestedContentKeys = (contentKey?: string): string[] | null => {
  if (typeof contentKey !== 'string') {
    return null;
  }

  const { locale } = getLocale();
  const keys = contentKey.split('.');
  const contentStr = keys.reduce<string | GenericObjectType>((content, key) => {
    // @ts-expect-error: TODO: Type isObject or move it into our app
    return isObject(content) ? content[key] : content;
  }, getContentFromCache(locale));

  if (typeof contentStr !== 'string') {
    return null;
  }

  // Regex look-behinds are not widely supported so we will need include { in
  // the regex and then map the matched strings to `.substring(1)` to get rid
  // of the preceding '{'
  const match = contentStr.match(NESTED_CONTENT_KEY_REGEX);
  return match ? match.map(str => str.substring(1)) : null;
};

export {
  getContent,
  getContentFromCache,
  getContentGroup,
  getContentHash,
  getNestedContentKeys,
  hasCachedContentForLocale,
  resetContent,
  updateContentCache,
};
